import clsx from 'clsx';
import { PIPELINE_STATUS_COLORS } from '@/types';

interface StatusProgressionProps {
  statuses: readonly string[];
  statusCounts: Record<string, number>;
  activeStatus: string | null;
  onStatusClick: (status: string | null) => void;
  onStatusHover: (status: string | null) => void;
  statusFilter: string | null;
  totalDeals: number;
}

export function StatusProgression({
  statuses,
  statusCounts,
  activeStatus,
  onStatusClick,
  onStatusHover,
  statusFilter,
  totalDeals,
}: StatusProgressionProps) {
  return (
    <div className="flex flex-col gap-1 min-h-0 h-full">
      {/* BY STATUS label — always visible, never scrolls away */}
      <div className="text-micro text-text-dim font-mono tracking-[0.8px] mb-px shrink-0">
        BY STATUS
      </div>

      {/* Status rows — scrollable safety net on short viewports */}
      <div className="overflow-y-auto min-h-0 flex flex-col gap-px">
        {statuses.map((s) => {
          const sc =
            PIPELINE_STATUS_COLORS[s as keyof typeof PIPELINE_STATUS_COLORS] ||
            'var(--color-text-dim)';
          const isActive = activeStatus === s;
          const count = statusCounts[s] || 0;
          return (
            <div
              key={s}
              onMouseEnter={() => {
                if (!statusFilter) onStatusHover(s);
              }}
              onMouseLeave={() => {
                if (!statusFilter) onStatusHover(null);
              }}
              onClick={() => onStatusClick(statusFilter === s ? null : s)}
              className={clsx(
                'flex items-center justify-between px-2 py-0.5 rounded-[2px] cursor-pointer transition-all duration-200',
                isActive ? 'border-l-2' : 'border-l-2 border-l-transparent'
              )}
              style={{
                background: isActive
                  ? `color-mix(in srgb, ${sc} 8%, transparent)`
                  : undefined,
                borderLeftColor: isActive ? sc : undefined,
              }}
              data-testid="status-row"
            >
              <div className="flex items-center gap-1.5">
                <span
                  className={clsx(
                    'w-1.5 h-1.5 rounded-full transition-opacity duration-200',
                    isActive ? 'opacity-100' : 'opacity-50'
                  )}
                  style={{ background: sc }}
                />
                <span
                  className={clsx(
                    'text-[10px] font-mono transition-all duration-200',
                    isActive
                      ? 'text-text-bright font-semibold'
                      : 'text-text-muted font-normal'
                  )}
                >
                  {s}
                </span>
              </div>
              <span
                className="text-[11px] font-mono font-semibold tabular-nums"
                style={{ color: isActive ? sc : 'var(--color-text-dim)' }}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>

      {/* Cap bar visualization — always visible below the list */}
      <div className="shrink-0 mt-1">
        <div className="flex h-1 rounded-[2px] overflow-hidden gap-px">
          {statuses.map((s) => {
            const pct =
              totalDeals > 0 ? ((statusCounts[s] || 0) / totalDeals) * 100 : 0;
            return (
              <div
                key={s}
                className="transition-opacity duration-200"
                style={{
                  width: `${pct}%`,
                  background:
                    PIPELINE_STATUS_COLORS[
                      s as keyof typeof PIPELINE_STATUS_COLORS
                    ],
                  opacity: activeStatus && activeStatus !== s ? 0.2 : 0.8,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
